import type {PropertyValues} from 'lit';
import {html} from 'lit';
import {customElement, property, state, queryAll} from 'lit/decorators.js';
import {msg} from '@lit/localize';
import {
  urlSearchParamsChanged$,
  maintenanceStatus$,
} from '../../stores/store-main.ts';
import {
  shareOrPrintSvg,
  notificationSvg,
  crossSvg,
} from '../../helpers/elements.ts';
import {unByKey} from 'ol/Observable.js';
import {createPopper} from '@popperjs/core/lib/popper-lite.js';
import popperOffset from '@popperjs/core/lib/modifiers/offset.js';
import popperPreventOverflow from '@popperjs/core/lib/modifiers/preventOverflow.js';
import popperFlip from '@popperjs/core/lib/modifiers/flip.js';
import type {EventsKey} from 'ol/events';
import {
  getLayout,
  getOptimalScale,
  getOptimalScaleForTour,
  getPrintExtent,
  ORIENTATIONS,
  postcompose,
  SCALES,
} from '../../helpers/print.ts';
import type {Orientations} from '../../helpers/print.ts';
import {classMap} from 'lit/directives/class-map.js';
import {map$} from '../../stores/store-map.ts';
import type Map from 'ol/Map';
import '../components/element-dropdown-list.ts';
import ElementSharePopup from '../element-share-popup.ts';
import './map-element-preview-map.ts';
import type {Extent} from 'ol/extent';
import {Tabs} from 'flowbite';
import type {TabItem, TabsInterface} from 'flowbite';
import {getShareLink} from '../../urlHandling/urlParams.ts';
import {getLanguageCode} from '../../i18n.ts';
import {dispatchMaintenanceButtonError} from '../../helpers/errorHandling.ts';
import {getMapKvp} from '../../urlHandling/urlSearchParams.ts';
import {sharePopupOpen$} from '../../stores/store-share-tour.ts';
import {
  activeTrackId$,
  activeTrackPoisCount$,
  currentlyActiveTourType$,
} from '../../stores/store-tours.ts';
import {splitClassList} from '../../helpers/classMap.ts';
import {zoomToTour} from '../../map/helpers.ts';
import VectorLayer from 'ol/layer/Vector.js';
import type VectorSource from 'ol/source/Vector';

const printSizes = [
  10000, 25000, 50000, 100000, 200000, 500000, 1000000, 1400000,
];
const sizeSelectItems = printSizes.map((v, k) => {
  return {
    title: () => `1 : ${v}`,
    value: v,
    default: k === 0,
  };
});

@customElement('element-share-or-print-popup')
export class ElementShareOrPrintPopup extends ElementSharePopup {
  @property({type: Boolean}) selectable = true;
  @state() scale: number = SCALES[0];
  @state() printActive = false;
  @state() printEnabled = false;
  @state() mobileView = false; // true if window width less than 820px (keep value same as in tailwind config)
  @state() largeView = false; // true if window width more than 1194px (keep value same as in tailwind config)
  @state() layout: string | undefined;
  @state() extent: Extent | undefined;
  @state() orientation: Orientations = 'landscape';
  private tabs: TabsInterface;
  private postcomposeListenerKey: EventsKey | undefined;
  private moveendListenerKey: EventsKey | undefined;
  @state() private map: Map | undefined;
  @state() private printValid = true;
  @state() private tourHasPois = activeTrackPoisCount$.value > 0;
  @state() private tourChecked = true;
  @queryAll('.check-print-tour') private checkboxesPrint: HTMLInputElement[];
  private resizeObserver: ResizeObserver;

  override connectedCallback(): void {
    super.connectedCallback();
    this.subscriptions.push(
      activeTrackPoisCount$.subscribe(
        (count) => (this.tourHasPois = count > 0),
      ),
      map$.subscribe((map) => (this.map = map)),
      sharePopupOpen$.subscribe((sharePopupOpen) => {
        if (
          (sharePopupOpen && !this.popupShown) ||
          (!sharePopupOpen && this.popupShown)
        )
          this.togglePopup();
      }),
    );
    this.resizeObserver = new ResizeObserver(() => {
      this.mobileView = window.matchMedia('(max-width: 820px)').matches;
      this.largeView = window.matchMedia('(min-width: 1194px)').matches;
    });
    this.resizeObserver.observe(document.body);
  }

  override updated(changedProperties: PropertyValues) {
    if (
      (changedProperties.has('isTourPublic') ||
        changedProperties.has('isEditing') ||
        changedProperties.has('printEnabled')) &&
      this.popper !== null
    ) {
      // updates the position of the popper when the share panel is open
      this.popper.update();
    }
    if (changedProperties.has('popupShown')) {
      // checks for animation in map buttons when toggle comes from plus menu
      const animationActive =
        this.parentElement.classList.contains('opacity-0');
      if (animationActive && this.popupShown) {
        setTimeout(() => {
          this.popper.update();
        }, 500);
      }
    }
    super.updated(changedProperties);
  }

  override disconnectedCallback() {
    this.printEnabled = false;
    this.resizeObserver.disconnect();
    this.subscriptions.forEach((s) => s.unsubscribe());
    super.disconnectedCallback();
  }

  override async firstUpdated() {
    const tabWrapper = this.querySelector<HTMLAreaElement>(
      '#share-or-print-tabs',
    );
    const tabElements: TabItem[] = [
      {
        id: 'share',
        triggerEl: this.querySelector('#share-tab-trigger'),
        targetEl: this.querySelector('#share-tab-content'),
      },
      {
        id: 'print-client-side',
        triggerEl: this.querySelector('#print-client-side-tab-trigger'),
        targetEl: this.querySelector('#print-client-side-tab-content'),
      },
      {
        id: 'print',
        triggerEl: this.querySelector('#print-tab-trigger'),
        targetEl: this.querySelector('#print-tab-content'),
      },
    ];

    const options = {
      activeClasses: 'bg-black text-white',
      inactiveClasses: 'bg-page-main text-black',
      onShow: async () => {
        if (!this.tabs) return;
        const activeTab = this.tabs.getActiveTab();
        this.printEnabled =
          ['print', 'print-client-side'].includes(activeTab.id) &&
          !maintenanceStatus$.getValue().logindisabled;
      },
    };
    this.tabs = new Tabs(tabWrapper, tabElements, options);

    const trackId = activeTrackId$.value;
    // disable poi checkbox if no pois
    if (this.isTourPage && trackId) {
      this.tourHasPois = activeTrackPoisCount$.value > 0;
    }
  }

  override togglePopup() {
    this.popupShown = !this.popupShown;
    sharePopupOpen$.next(this.popupShown);
    const options = {
      detail: this.popupShown,
      bubbles: true,
      composed: true,
    };
    this.dispatchEvent(new CustomEvent('togglePopup', options));

    if (this.popupShown) {
      const targetEl = this.querySelector<HTMLElement>('.share-popup');
      const triggerEl = this.querySelector<HTMLElement>('.dropdown-trigger');
      popperOffset.options = {
        offset: () => {
          // discount shift for different share panels
          const shiftDiscount =
            (!this.isTourPublic || this.isEditing) && this.isTourPage ? 120 : 0;
          if (this.mobileView) {
            return [200 - 2 * shiftDiscount, 10];
          }
          if (this.largeView) {
            return [310 - shiftDiscount, 20];
          }
          return [234 - 2 * shiftDiscount, 20];
        },
      };
      this.popper = createPopper(triggerEl, targetEl, {
        placement: 'left-start',
        modifiers: [popperPreventOverflow, popperOffset, popperFlip],
        onFirstUpdate: () => this.popper.forceUpdate(), // IOS15 position fix
      });
      this.urlParamsSubscription = urlSearchParamsChanged$.subscribe(
        (params) => {
          this.shareLink = this.isTourPage
            ? (this.shareLink = location.href)
            : getShareLink(getLanguageCode(), location.href, params);
        },
      );
    } else {
      this.popper.destroy();
      this.popper = null;
      this.urlParamsSubscription?.unsubscribe();
    }
    const printItem = ['print', 'print-client-side'].includes(
      this.tabs.getActiveTab().id,
    );
    this.printEnabled =
      this.popupShown &&
      printItem &&
      !maintenanceStatus$.getValue().logindisabled;
  }

  private togglePrint() {
    if (this.printEnabled && this.showMask) {
      if (this.isTourPage) {
        const trackLayer = this.map
          ?.getAllLayers()
          .find(
            (l): l is VectorLayer<VectorSource<any>, any> =>
              l instanceof VectorLayer && l.get('name') === 'trackLayer',
          );
        zoomToTour(this.map, trackLayer);
        this.scale = getOptimalScaleForTour(
          this.map,
          this.orientation,
          trackLayer.getSource(),
        );
      } else {
        this.scale = getOptimalScale(this.map, this.orientation);
      }
      ORIENTATIONS.forEach((o) => {
        const el = this.querySelector<HTMLElement>(`#radio-print-${o}`);
        if (o === this.orientation && el) el.click();
      });
      this.postcomposeListenerKey = this.map.on('postcompose', (evt) => {
        postcompose(evt, this.map, this.scale, this.orientation);
      });
      this.moveendListenerKey = this.map.on('moveend', () => {
        this.extent = getPrintExtent(this.map, this.layout, this.scale);
      });
    } else {
      unByKey(this.postcomposeListenerKey);
      this.postcomposeListenerKey = undefined;
      unByKey(this.moveendListenerKey);
      this.moveendListenerKey = undefined;
    }
    this.map.render();
  }

  override update(changedProperties: PropertyValues) {
    if (this.map) {
      if (
        changedProperties.has('printEnabled') ||
        changedProperties.has('showMask')
      ) {
        this.togglePrint();
      }
      if (
        changedProperties.has('popupShown') &&
        !this.popupShown &&
        this.printEnabled
      ) {
        this.printEnabled = false;
        this.togglePrint();
      }
      if (
        (changedProperties.has('orientation') ||
          changedProperties.has('scale') ||
          changedProperties.has('printEnabled')) &&
        this.printEnabled
      ) {
        this.layout = getLayout(this.map, this.orientation);
        this.extent = getPrintExtent(this.map, this.layout, this.scale);
        this.map.render();
      }
    }
    super.update(changedProperties);
  }

  private checkPrintValid() {
    const checkboxesArray = Array.from(this.checkboxesPrint);
    if (checkboxesArray.some((checkbox) => checkbox.checked)) {
      this.printValid = true;
    } else {
      this.printValid = false;
    }
    this.tourChecked = this.checkboxesPrint[0].checked;
  }

  private printMapClientSide() {
    // use same extent as preview map
    this.map.getView().fit(this.extent);
    const kvp = getMapKvp(this.map.getView());

    const tourType = currentlyActiveTourType$.value;
    const trackId = activeTrackId$.value;
    if (trackId && tourType === 'normal') {
      kvp.append('tourId', trackId.toString());
    } else if (trackId && tourType === 'recordedtrack') {
      kvp.append('recordedTourId', trackId.toString());
    }
    kvp.append('orientation', this.orientation);
    kvp.append('title', document.title);

    kvp.delete('photos');
    kvp.append('photos', 'false');
    kvp.append('scale', this.scale.toString());

    const printUrl = `/${getLanguageCode()}/print?${kvp.toString()}`;
    window.open(printUrl, '_blank', 'noreferrer');
  }

  renderShareOrPrintButton() {
    return html`<button
      tabindex="${this.selectable ? 0 : -1}"
      aria-label="${msg('Share or print')}"
      @click="${() => this.togglePopup()}"
      class="${classMap({
        'bg-white': !this.popupShown,
        'bg-chm-focus-red': this.popupShown,
      })} dropdown-trigger group select-none rounded-full p-2.5 shadow-card active:bg-chm-focus-red w-nav-button-s mdxx:w-nav-button-l mouse:hover:bg-chm-focus-red"
    >
      ${shareOrPrintSvg(
        `${
          this.popupShown ? 'fill-white' : 'fill-chm-red'
        } group-active:fill-white mouse:group-hover:fill-white`,
      )}
    </button>`;
  }

  renderPreviewMap(disabled = false) {
    return html`
      <element-dropdown-list
        .items=${sizeSelectItems}
        .value="${this.scale}"
        .isElementSharePopup=${true}
        .disabled=${this.printActive || disabled}
        @changed=${(evt: CustomEvent) => {
          this.scale = evt.detail.selected?.value;
        }}
      >
      </element-dropdown-list>
      <div
        class="mb-4 mt-[20px] flex items-center gap-x-[14px] sm:gap-x-[18px] md:mt-[30px] mdxx:gap-x-[23px]"
      >
        <div
          class="body-links-and-text hidden md:block ${classMap({
            'text-chm-gray': disabled,
          })}"
        >
          ${msg('Format')}:
        </div>
        ${ORIENTATIONS.map(
          (orientation) => html`
            <div
              class="flex items-center ${classMap({
                'pointer-events-none': disabled,
              })}"
            >
              <input
                .checked="${this.orientation === orientation}"
                id="radio-print-${orientation}"
                type="radio"
                value="${orientation}"
                name="${orientation}"
                class="h-radio w-radio cursor-pointer focus-visible:ring focus-visible:ring-focus-visible"
                ?disabled=${this.printActive || disabled}
                @change="${() => (this.orientation = orientation)}"
                @keydown="${(e: KeyboardEvent) => {
                  if (e.code === 'Enter') (<HTMLElement>e.target).click();
                }}"
              />
              <label
                for="radio-print-${orientation}"
                class="body-links-and-text ${classMap({
                  'text-chm-gray': this.printActive,
                })} ml-2 cursor-pointer"
                @click=${(e: PointerEvent) => {
                  e.preventDefault();
                  this.querySelector<HTMLElement>(
                    `#radio-print-${orientation}`,
                  ).click();
                }}
                >${orientation === 'landscape'
                  ? msg('Landscape')
                  : msg('Portrait')}</label
              >
            </div>
          `,
        )}
      </div>
      <div class="flex justify-center">
        <div
          class="${classMap(
            splitClassList({
              'w-[181px] md:w-[255px]': this.orientation === 'landscape',
              'w-[81px] md:w-[114px]': this.orientation === 'portrait',
              'grayscale opacity-50': disabled,
            }),
          )} relative h-[114px] md:h-[160px]"
        >
          <element-preview-map
            id="print-map"
            class="block h-full"
            .extent="${this.extent}"
            .orientation="${this.orientation}"
          >
          </element-preview-map>
        </div>
      </div>
    `;
  }

  renderPrintBody() {
    return this.isEditing
      ? html`
          <div class="flex bg-chm-light-gray h-auto p-[12px]">
            <div class="mr-[12px]">
              ${notificationSvg('fill-chm-gray w-[24px] h-[24px]')}
            </div>
            <div class="text-body md:text-md-body">
              ${msg(
                'To share a tour, please save it first and then exit edit mode.',
                {desc: 'Share tour panel in edit mode'},
              )}
            </div>
          </div>
        `
      : html`
          ${this.renderPreviewMap()}
          <div class="mt-[20px] flex justify-end md:mt-[24px]">
            <button
              class="${classMap({
                'pseudo-disabled': this.printActive || !this.printEnabled,
                'bg-loader-animated-white':
                  this.printActive || !this.printEnabled,
                'text-transparent': this.printActive || !this.printEnabled,
                'bg-primary-gray': this.printActive || !this.printEnabled,
                'bg-[length:28px]': this.printActive || !this.printEnabled,
                'hover:bg-primary-gray': this.printActive || !this.printEnabled,
                'mouse:hover:bg-chm-focus-red':
                  !this.printActive && this.printEnabled,
              })} button-color-red h-nav-button-s bg-center
          bg-no-repeat md:h-nav-button-l"
              @click=${() => {
                if (this.printActive || !this.printEnabled) {
                  dispatchMaintenanceButtonError(this.dispatchEvent.bind(this));
                } else {
                  this.printMapClientSide();
                }
              }}
            >
              ${msg('Print map')}
            </button>
          </div>
        `;
  }

  renderPrintClientSideBody() {
    return this.isEditing
      ? html`
          <div class="flex bg-chm-light-gray h-auto p-[12px]">
            <div class="mr-[12px]">
              ${notificationSvg('fill-chm-gray w-[24px] h-[24px]')}
            </div>
            <div class="text-body md:text-md-body">
              ${msg(
                'To share a tour, please save it first and then exit edit mode.',
                {desc: 'Share tour panel in edit mode'},
              )}
            </div>
          </div>
        `
      : html` <form @submit="${this.openPrintWindow}">
          <div class="flex flex-col gap-[12px] w-full">
            <div>
              <label
                class="select-none text-body sm:text-sm-body md:text-md-body cursor-pointer"
              >
                <input
                  type="checkbox"
                  id="checkbox-print-tour"
                  name="checkbox-print-tour"
                  class="h-radio w-radio mr-[6px] check-print-tour"
                  .checked=${true}
                  @change=${() => {
                    this.checkPrintValid();
                  }}
                />
                ${msg('Tour')}
              </label>
            </div>
            <div>${this.renderPreviewMap(!this.tourChecked)}</div>
            <div>
              <label
                class="select-none text-body sm:text-sm-body md:text-md-body cursor-pointer"
              >
                <input
                  type="checkbox"
                  id="checkbox-print-profile"
                  name="checkbox-print-profile"
                  class="h-radio w-radio mr-[6px] check-print-tour"
                  @change=${() => {
                    this.checkPrintValid();
                  }}
                />
                ${msg('Height profile')}
              </label>
            </div>
            <div>
              <label
                class="select-none text-body sm:text-sm-body md:text-md-body cursor-pointer ${classMap(
                  splitClassList({
                    'text-chm-gray pointer-events-none': !this.tourHasPois,
                  }),
                )}"
              >
                <input
                  type="checkbox"
                  id="checkbox-print-pois"
                  name="checkbox-print-pois"
                  class="h-radio w-radio mr-[6px] check-print-tour"
                  .disabled=${!this.tourHasPois}
                  @change=${() => {
                    this.checkPrintValid();
                  }}
                />
                ${msg('Marker')}
              </label>
            </div>
          </div>

          <div class="mt-[20px] flex justify-end md:mt-[24px]">
            <button
              type="submit"
              class="button-color-red h-nav-button-s bg-center bg-no-repeat md:h-nav-button-l ${classMap(
                splitClassList({
                  'disabled bg-primary-gray text-white': !this.printValid,
                }),
              )}"
            >
              ${msg('Print tour', {desc: 'Tour print popup'})}
            </button>
          </div>
        </form>`;
  }

  private openPrintWindow(event: SubmitEvent) {
    event.preventDefault();
    const kvp = getMapKvp(this.map.getView());
    const tourType = currentlyActiveTourType$.value;
    const trackId = activeTrackId$.value;
    if (tourType && trackId)
      kvp.append(
        tourType === 'normal' ? 'tourId' : 'recordedTourId',
        trackId.toString(),
      );
    kvp.append('printType', 'tour');
    const form = event.target as HTMLFormElement;
    if (
      (<HTMLInputElement>form.elements.namedItem('checkbox-print-tour')).checked
    ) {
      kvp.append('printTour', 'true');
    }
    if (
      (<HTMLInputElement>form.elements.namedItem('checkbox-print-profile'))
        .checked
    ) {
      kvp.append('printProfile', 'true');
    }
    if (
      (<HTMLInputElement>form.elements.namedItem('checkbox-print-pois')).checked
    ) {
      kvp.append('printPois', 'true');
    }
    kvp.append('title', document.title);
    kvp.append('orientation', this.orientation);
    kvp.append('scale', this.scale.toString());

    const printUrl = `/${getLanguageCode()}/print?${kvp.toString()}`;

    window.open(printUrl, '_blank', 'noreferrer');
  }

  override render() {
    return html`
      ${this.renderShareOrPrintButton()}
      <div
        .hidden="${!this.popupShown}"
        class="z-50 share-popup absolute w-[245px] xxs:w-[294px] md:w-[424px] select-none h-auto"
      >
        <div class="block h-full bg-[#F8F9FA] p-[16px] shadow-popup">
          <div class="h-[24px] mb-[16px] flex items-center">
            <div class="body-links-and-text font-bold">
              ${this.isTourPage ? msg('Tour') : msg('Map')}:
            </div>
            <button
              class="flex place-items-center cursor-pointer ml-auto distinction link-with-icon-in-context mouse:hover:text-chm-focus-red"
              @click="${this.togglePopup}"
            >
              ${crossSvg(
                'h-[24px] w-[24px] fill-chm-red mouse:group-hover:fill-chm-focus-red',
              )}
            </button>
          </div>
          <div
            class="mb-[20px] flex items-center gap-x-[12px] md:mb-[24px] md:gap-x-[16px]"
          >
            <div
              role="tablist"
              id="share-or-print-tabs"
              tabindex="0"
              class="flex flex-nowrap gap-[8px] overflow-hidden"
            >
              <button
                role="tab"
                tabindex="0"
                id="share-tab-trigger"
                class="overflow-hidden text-ellipsis whitespace-nowrap rounded-full px-4  focus-visible:bg-chm-focus-red focus-visible:text-white focus-visible:outline-none focus-visible:ring focus-visible:ring-chm-red text-tab md:text-md-tab mdxx:text-mdxx-tab mouse:hover:bg-chm-focus-red mouse:hover:text-white"
              >
                ${msg('Share')}
              </button>
              <button
                role="tab"
                tabindex="0"
                id="print-tab-trigger"
                class="${classMap({
                  hidden: this.isTourPage,
                })} overflow-hidden text-ellipsis whitespace-nowrap rounded-full px-4  focus-visible:bg-chm-focus-red focus-visible:text-white focus-visible:outline-none focus-visible:ring focus-visible:ring-chm-red text-tab md:text-md-tab mdxx:text-mdxx-tab mouse:hover:bg-chm-focus-red mouse:hover:text-white"
              >
                ${msg('Print map')}
              </button>
              <button
                role="tab"
                tabindex="0"
                id="print-client-side-tab-trigger"
                class="${classMap({
                  hidden: !this.isTourPage,
                })} overflow-hidden text-ellipsis whitespace-nowrap rounded-full px-4  focus-visible:bg-chm-focus-red focus-visible:text-white focus-visible:outline-none focus-visible:ring focus-visible:ring-chm-red text-tab md:text-md-tab mdxx:text-mdxx-tab mouse:hover:bg-chm-focus-red mouse:hover:text-white"
              >
                ${msg('Print map')}
              </button>
            </div>
          </div>
          <div class="col-span-full">
            <div role="tabpanel" id="share-tab-content">
              ${this.renderShareBody()}
            </div>
            <div role="tabpanel" id="print-tab-content">
              ${this.renderPrintBody()}
            </div>
            <div role="tabpanel" id="print-client-side-tab-content">
              ${this.renderPrintClientSideBody()}
            </div>
          </div>
        </div>
      </div>
    `;
  }
}

declare global {
  interface HTMLElementTagNameMap {
    'element-share-or-print-popup': ElementShareOrPrintPopup;
  }
}
